import React, { memo, useCallback, useEffect } from 'react'
import { SecureWrapper } from '../layout/SecureWrapper'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  CompleteLMSTrainingStep,
  FetchLMSTrainingState,
  FetchDriver,
} from 'src/constants/actionTypes'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { LMSTrainingCollectDataStep } from 'src/interfaces/collectData'
import { handleApiError } from 'src/utils/errorHandlers'
import { LoadingPage } from 'src/pages/LoadingPage'
import tw from 'twin.macro'
import { NoteMajor } from '@shopify/polaris-icons'
import { CollectDataStepIndex } from '../client/CollectDataStepIndex'
import { Typography } from '@mui/material'
import { Button } from '../shared/Button'
import { GSIcon } from '../shared/GSIcon'
import { useQuery } from 'src/utils/hooks/useQuery'
import Lottie from 'lottie-react'
import createCertificateAnimation from 'src/assets/animations/create-certificate.json'
import { usePolling } from 'src/utils/hooks/usePolling'
import { useNavigate } from 'react-router-dom'
import { CLIENT_ROUTES } from 'src/constants/routes'

const TRAINING_INFO_UPDATE_POLLING_INTERVAL = 1000 * 5

const LMSTrainingComponent = () => {
  const dispatch = useDispatch()
  const { getQuery } = useQuery()
  const navigate = useNavigate()

  const currentStep = useSelector(getCurrentCollectDataStep) as LMSTrainingCollectDataStep

  const [isLoaded, setIsLoaded] = React.useState(false)
  const [certificateUrl, setCertificateUrl] = React.useState('')
  const [signedUrl, setSignedUrl] = React.useState('')

  const lmsReturnEvent = getQuery('event')

  const isTrainingCompleted = lmsReturnEvent === 'lms_training_completed'

  const fetchTrainingInfo = useCallback(async () => {
    try {
      const { certificateUrl, signedUrl }: { certificateUrl?: string; signedUrl: string } =
        await createAsyncAction(
          dispatch,
          FetchLMSTrainingState.request({
            lmsTrainingId: currentStep.metadata.lmsTrainingId,
            stepId: currentStep.id,
          }),
        )

      setCertificateUrl(certificateUrl || '')
      setSignedUrl(signedUrl)

      if (certificateUrl) {
        navigate(CLIENT_ROUTES.DOCS_URL)
      }
    } catch (error) {
      handleApiError(error)
    }

    setIsLoaded(true)
  }, [dispatch, currentStep.metadata.lmsTrainingId, currentStep.id])

  useEffect(() => {
    fetchTrainingInfo()
  }, [fetchTrainingInfo])

  const { startPolling: trainingStartPolling, stopPolling: trainingStopPolling } = usePolling(
    () => {
      fetchTrainingInfo()

      dispatch(FetchDriver.request())
    },
    TRAINING_INFO_UPDATE_POLLING_INTERVAL,
  )

  useEffect(() => {
    if (!isTrainingCompleted || certificateUrl) {
      trainingStopPolling()

      return
    }

    trainingStartPolling()

    return () => {
      trainingStopPolling()
    }
  }, [currentStep, trainingStartPolling, trainingStopPolling])

  const handleStartTraining = useCallback(() => {
    const redirectUrl = `${window.location.origin}/client/documents?event=lms_training_completed`

    window.open(`${signedUrl}&redirect_back=${redirectUrl}`, '_self')
  }, [signedUrl])

  const handleCompleteStep = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        CompleteLMSTrainingStep.request({
          stepId: currentStep.id,
        }),
      )

      await createAsyncAction(dispatch, FetchDriver.request())
    } catch (error) {
      handleApiError(error)
    }
  }, [dispatch, currentStep.id])

  if (!isLoaded || !signedUrl) {
    return <LoadingPage flow="client" />
  }

  return (
    <SecureWrapper>
      {!certificateUrl && !isTrainingCompleted && (
        <div css={tw`h-full w-full flex flex-col justify-between`}>
          <div>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <NoteMajor width={20} />
            </div>
            <CollectDataStepIndex stepId={currentStep.id} />
            <Typography css={tw`mb-2 mt-2`} variant="h2">
              Certification Step
            </Typography>
            <Typography css={tw`text-shades-400`}>
              {`Let's start certification process. After clicking the button below you will be
              redirected to complete "${currentStep?.metadata?.lmsTrainingName}" course on our learning platform, where you
              will be guided through all required steps to complete it.`}
            </Typography>
            <Typography css={tw`text-shades-400 mb-4`}>
              Once you complete your certification, it will be automatically added to your profile,
              and you'll also be able to download it.
            </Typography>
          </div>
          <Button onClick={handleStartTraining}>Start Certification Process</Button>
        </div>
      )}
      {certificateUrl && (
        <div css={tw`h-full w-full flex flex-col justify-between`}>
          <div>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <NoteMajor width={20} />
            </div>
            <CollectDataStepIndex stepId={currentStep.id} />
            <Typography css={tw`mb-2 mt-2`} variant="h2">
              Certification Completed!
            </Typography>
            <Typography css={tw`text-shades-400`}>
              We have received your certificate successfully.
            </Typography>
            <Typography css={tw`text-shades-400`}>
              You can now proceed to the next step. You can also download your certificate here:
            </Typography>
            <a css={tw`no-underline`} href={certificateUrl} target="_blank">
              <div
                css={tw`flex mt-4 items-center flex-row p-4 rounded-2xl bg-shades-0 cursor-pointer`}
              >
                <div
                  css={tw`h-11 w-11 rounded-full items-center justify-center flex bg-shades-100 mr-4`}
                >
                  <GSIcon iconName="gs_file_download" />
                </div>
                <div>
                  <Typography css={tw`text-shades-1000`} variant="h4">
                    Your certificate
                  </Typography>
                  <Typography css={tw`mt-0.5 text-primary-500`} variant="caption">
                    Tap to view
                  </Typography>
                </div>
              </div>
            </a>
          </div>
          <Button onClick={handleCompleteStep}>Continue</Button>
        </div>
      )}
      {!certificateUrl && isTrainingCompleted && (
        <div css={tw`h-full w-full flex flex-col justify-between`}>
          <div>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <NoteMajor width={20} />
            </div>
            {currentStep && <CollectDataStepIndex stepId={currentStep.id} />}
            <Typography css={tw`mb-2 mt-2`} variant="h2">
              Certification Completed!
            </Typography>
            <Typography css={tw`text-shades-400`}>
              We're creating your certificate. This will only take a moment — please don't close the
              window.
            </Typography>
            <div css={tw`self-center mt-4 px-16`}>
              <Lottie animationData={createCertificateAnimation} loop={true} />
            </div>
          </div>
          <Button disabled>Continue</Button>
        </div>
      )}
    </SecureWrapper>
  )
}

export const LMSTraining = memo(LMSTrainingComponent)
